import React, { useEffect, useState } from 'react';
import { CalendarDays } from 'lucide-react';
import { brandKnowledgeApi, PlanBriefRow } from '../../services/brandKnowledgeApi';

/**
 * Shown on a campaign made from a monthly-plan brief. The brief itself reaches the
 * agent through brand context; this lets the owner start from it in one click, or
 * apply the brief's latest version after it was edited in Brand Kit.
 */
export const PlanBriefBanner: React.FC<{
  conversationId?: string;
  hasMessages: boolean;
  loading: boolean;
  onSend: (text: string, displayText: string) => Promise<void> | void;
}> = ({ conversationId, hasMessages, loading, onSend }) => {
  const [row, setRow] = useState<(PlanBriefRow & { out_of_date: boolean }) | null>(null);
  useEffect(() => {
    setRow(null);
    if (!conversationId) return;
    brandKnowledgeApi.briefForCampaign(conversationId).then(setRow).catch(() => setRow(null));
  }, [conversationId]);
  if (!row || (hasMessages && !row.out_of_date)) return null;
  const b = row.brief;

  const start = () => onSend(
    `Create this campaign from my approved plan brief "${b.name}". Use its channel, dates, budget, audience, message, offer, angle and call to action.`,
    `Start from plan brief: ${b.name}`,
  );
  const apply = async () => {
    await onSend('My plan brief for this campaign changed. Update the campaign to match the latest brief.', `Apply the updated brief: ${b.name}`);
    try { await brandKnowledgeApi.linkBriefCampaign(row.id, conversationId!); setRow({ ...row, out_of_date: false }); } catch { /* shown again next time */ }
  };

  return (
    <div className="mx-4 mb-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2.5 text-sm flex flex-wrap items-center justify-between gap-2">
      <div className="flex items-start gap-2 min-w-0">
        <CalendarDays className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
        <div className="min-w-0">
          <div className="font-medium text-ink">{row.out_of_date ? 'The plan brief changed' : 'Built from your monthly plan'}</div>
          <div className="text-ink-2 truncate">{b.name} · {b.segment} · {b.message}</div>
        </div>
      </div>
      <button type="button" disabled={loading} onClick={row.out_of_date ? apply : start}
        className="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-dark disabled:opacity-50">
        {row.out_of_date ? 'Apply changes' : 'Start this campaign'}
      </button>
    </div>
  );
};
